/**
 * Copyright (c) 2026, CheckPlus Limited
 */

/**
 * Simplified Chinese copy for the Plug marketing site.
 *
 * Imported from the I18N table in `Plug Landing.dc.html` (Claude Design
 * project 3d0c2fbb), which now carries a proofread trilingual pass — see
 * `Plug Landing - Chinese Translation.md` in the same project for the
 * reviewer-facing table.
 *
 * Where copyEn.js carries a section 7b correction the design has not taken up
 * — the platform-fee wording, the three business rule types, and the figures
 * that would claim a launched product — the English has no counterpart in the
 * design's table, so the translation here follows copyEn.js rather than the
 * design. Correct all three languages together.
 *
 * Terminology follows the design's own glossary: 捐助规则 (giving rule),
 * 捐款计划 (giving plan), 慈善义卖 (charity sale), 单位成本 (unit cost),
 * 非营利机构 (nonprofit), 税务条例第 88 条 (Section 88), 审计师 (auditor).
 */

const copyCn = {
  documentTitle: "Plug",

  nav: {
    about: "什么是 Plug",
    donors: "捐款者",
    business: "商户",
    nonprofit: "非营利机构",
    login: "登录",
    logoAlt: "Plug",
    beta: "BETA",
    cta: {
      about: "注册",
      donors: "注册",
      business: "立即开始",
      nonprofit: "加入 Plug",
    },
  },

  about: {
    pill: "一个联系商户、非营利机构与捐款者的公益平台",
    h1: "Plug：联系社会各方的公益平台\n让行善更简单、更持续",
    lede:
      "Plug 将商户、非营利机构与捐款者连结于同一生态圈，令捐款更方便、更持续。商户可以将行善与营业额挂钩、融入日常营运；捐款者以最适合自己的方式支持所关心的慈善工作；" +
      "非营利机构则获得更多、更稳定的资助机会、提高曝光，并以更清晰的方式展示工作成效。",
    ctaPrimary: "浏览非营利机构",
    ctaSecondary: "了解 Plug 如何联系三方",
    flow: {
      kicker: "PLUG 如何联系并支援三方",
      title: "一个互联互通的捐款流程，为三方创造更多价值。",
      business: {
        name: "商户",
        txt: "在 Plug 上轻松建立及管理不同类型的捐助计划规则。",
      },
      donors: {
        name: "捐款者",
        txt: "轻松建立捐款计划，支持不同非营利机构，并可随时调整。",
      },
      nonprofits: {
        name: "非营利机构",
        txt: "通过 Plug 让商户和捐款者能够更便利地进行捐款，从而获得更多、更稳定的资助；同时向支持者展示捐款带来的成果，以吸引更多支持者。",
      },
      hub: "核实非营利机构资格、简化捐款流程、透过 Stripe 处理付款、推动慈善公益",
      bizOut: "捐出部分营业额、慈善义卖或更多",
      bizBack: "企业社会责任实证、自动化行政流程、提升顾客参与度",
      donorOut: "定期或单次捐款",
      donorBack: "自动化处理、税务收据、非营利机构最新动态",
      npDown: "获得更多资助机会、简化行政、提升机构知名度",
      npUp: "工作成果与公益成效更新",
    },
    sides: [
      {
        id: "business",
        kicker: "商户得到什么",
        title: "让客户看得见的企业社会责任。",
        txt:
          "在商户结账页面及 Plug 商户专页上显示你的捐助计划（如捐出此订单总额的 1%），让顾客了解其消费所支持的公益项目，甚至与之互动。" +
          "Plug 协助商户提升顾客参与度、购买意欲及品牌声誉，同时处理捐款，并把企业社会责任纪录集中管理。",
        link: "Plug 商户方案 →",
      },
      {
        id: "donors",
        kicker: "捐款者得到什么",
        title: "灵活捐款，所有纪录集中管理。",
        txt:
          "建立一个或多个捐款计划，自订捐款金额及频率，并将捐款分配予信任的非营利机构。你可以随时调整或暂停计划、在账户内保存收据、接收慈善机构最新消息，" +
          "并支持回馈社会的商户。",
        link: "Plug 捐款者方案 →",
      },
      {
        id: "nonprofit",
        kicker: "非营利机构得到什么",
        title: "更多资助，更少的行政工作。",
        txt:
          "商户与捐款者均可透过 Plug 找到你的机构。Plug 让与营业额挂钩的商户捐款及个人定期捐款更容易实行。你可透过商户捐款规则、" +
          "捐款计划及慈善义卖获得支持，而你的 Plug 机构专页与公益成效更新则有助吸引下一位支持者。",
        link: "Plug 非营利机构方案 →",
      },
    ],
    valuesTitle: "Plug 的四大优势",
    values: [
      {
        title: "简单开始，轻松管理。",
        txt: "在 Plug 上轻松建立捐款规则及捐款计划。付款处理、捐款分配及收据纪录均可自动处理。",
      },
      {
        title: "为持续行善而设",
        txt: "Plug 透过顾客互动与公益成效实证，让商户的捐助带来更高的企业社会责任价值，令他们有更充分的理由持续捐助。",
      },
      {
        title: "更公平的支援机会",
        txt: "不论规模大小，非营利机构都能在 Plug 上被看见，并透过分享工作成效，帮助潜在捐款人了解和支持其工作。",
      },
      {
        title: "全程经过审核",
        txt: "机构上架前须被核实其第 88 条免税资格；收款与付款均经 Stripe 处理，Plug 将不会持有任何款项。",
      },
    ],
  },

  donors: {
    pill: "人人都可以行善 · 0 家第 88 条认可非营利机构",
    h1: "把每次消费的一小部分，化为善意。",
    lede: "设定一个金额，按你的心意分配给任意数量的香港非营利机构，Plug 以单一付款完成转账，所有收据集中保存。每月最低 HK$100，亦可单次捐款。",
    ctaPrimary: "开始捐款",
    ctaSecondary: "浏览非营利机构",
    note: "免费加入、没有最低金额，任何月份都可更改或停止。付款成功后即可收到收据。",
    shot: "应用程序画面 · 你的捐款计划",
    press: {
      label: "媒体报道",
      logo: "标志",
    },
    how: {
      kicker: "运作方式",
      h2: "三个决定，之后自动运作。",
      lede: "大部分人两分钟内完成设定便不用再理会，当然你亦可随时调整分配。",
      steps: [
        {
          title: "支持你关心的公益范畴",
          txt: "按机构实际的服务范畴浏览——无家可归、饥饿、残疾、精神健康——再加入你信任的机构。",
          shot: "画面 · 探索",
        },
        {
          title: "设定总额并分配",
          txt: "拖动任一比重，其余会自动重新平衡，令分配总和永远是 100%。只支持一家机构？那就完全不用设定百分比。",
          shot: "画面 · 分配",
        },
        {
          title: "即时取得收据",
          txt: "付款成功即发出第 88 条扣税收据，通常只需数秒，并附上成效报告，说明你的捐款换来了什么。",
          shot: "画面 · 成效",
        },
      ],
    },
    calc: {
      kicker: "立即试算 — 无须开立账户",
      h2: "你的捐款可以换来什么？",
      lede: "以 Plug 上三家非营利机构公布的真实单位成本计算，并按大部分新捐款者的分配方式划分。",
      label: "我可以捐出",
      currency: "HK$",
      per: "/ 每月",
      footBefore: "即全年",
      footAfter: "，分配给三家非营利机构。",
      yearly: "／年",
      photo: "照片预留位置",
    },
    onePayment: {
      kicker: "一次付款，一站管理",
      h2: "一次过支持多个公益项目，所有收据集中保存。",
      lede: "没有人想为每间支持的机构分别设立自动转账、账户登录和 PDF 文件。Plug 把这一切整合为单一付款与单一纪录。",
      give: {
        title: "支持多间，只付一次",
        txt: "在同一计划中加入任意数量的非营利机构。总额以单一付款收取，再按你设定的比重分配——不用逐笔交易，也不用重复输入信用卡资料。",
        oneLabel: "单一付款",
        oneValue: "HK$400 / 每月",
        checks: [
          "比重总和永远为 100%——调整其一，其余自动平衡",
          "增加或移除机构，总额维持不变",
        ],
      },
      receipts: {
        title: "所有收据，一处存放",
        txt: "每次成功付款都会产生一张涵盖所有受助机构的第 88 条收据，付款完成即存入你的账户。报税时只需下载整年纪录，不用翻查一堆邮件。",
        label: "你的收据 · 2026",
        note: "符合第 88 条扣税资格",
        pdf: "PDF",
        totalLabel: "年初至今",
        totalValue: "HK$3,200",
        rows: [
          {
            date: "2026 年 8 月 1 日",
            detail: "RC-2608 · 4 家非营利机构",
            amount: "HK$400",
          },
          {
            date: "2026 年 7 月 1 日",
            detail: "RC-2607 · 4 家非营利机构",
            amount: "HK$400",
          },
          {
            date: "2026 年 6 月 12 日",
            detail: "RC-2606b · 单次捐款，1 家非营利机构",
            amount: "HK$1,000",
          },
          {
            date: "2026 年 6 月 1 日",
            detail: "RC-2606 · 3 家非营利机构",
            amount: "HK$400",
          },
        ],
        checks: [
          "付款成功即发出，通常只需数秒",
          "一个档案下载全年纪录，方便报税",
        ],
      },
    },
    marketplace: {
      kicker: "公益平台",
      h2: "0 家非营利机构，按服务范畴分类。",
      lede: "每家机构均按税务局第 88 条名单核实，并在档案内公开经审计账目及单位成本。",
      browse: "浏览所有非营利机构",
      countSuffix: "家机构",
    },
    impact: {
      kicker: "本年度成效",
      h2: "日常消费，化成宿位、餐膳与照顾。",
      lede: "由各非营利机构自行呈报，并按其档案上公布的单位成本计算。",
      stats: [
        {
          num: "21,400",
          txt: "个深水埗及油麻地宿位晚上",
        },
        {
          num: "468,000",
          txt: "份剩食回收并送到家庭手上",
        },
        {
          num: "3,100",
          txt: "小时资助辅导，轮候不超过两星期",
        },
        {
          num: "640",
          txt: "名残疾成人成功投入公开就业",
        },
      ],
      quote: {
        txt: "“我设定每月 HK$200 分给三家机构，之后就忘记了。十一个月后，有一页告诉我，这些捐款换来了八个晚上的宿位和一百六十份餐膳。”",
        mono: "TL",
        name: "Tania L.",
        meta: "自 2025 年 10 月起捐款 · 坚尼地城",
        photo: "照片",
      },
    },
    trust: [
      {
        kicker: "收费说明",
        title: "免费加入，免费上架。",
        txt: "费用只会从实际到账的捐款中扣除——不会因为你在这里而收取月费。",
      },
      {
        kicker: "核实而非随意上架",
        title: "已向税务局核实第 88 条资格",
        txt: "机构须先核实慈善团体编号、经审计账目及服务成本，才可收取任何款项。",
      },
      {
        kicker: "资金流向",
        title: "由 Stripe 结算",
        txt: "款项从不停留在我们手上。付款成功即发出收据，拨款会合并为一笔清晰的转账交予机构。",
      },
    ],
    cross: [
      {
        id: "business",
        kicker: "你是商户？",
        title: "自动捐出部分收入。",
        txt: "公布捐助规则、在店面展示，并自动取得企业社会责任报告。",
        link: "了解 Plug 商户方案 →",
      },
      {
        id: "nonprofit",
        kicker: "你是非营利机构？",
        title: "稳定的定期支持，账目清晰完整。",
        txt: "以第 88 条编号登记上架，清楚看见哪些个人与商户正在支持你。",
        link: "了解 Plug 非营利机构方案 →",
      },
    ],
  },

  business: {
    pill: "0 家香港商户正在 Plug 上行善",
    h1: "订立一条捐助规则，其余交给你的营业额。",
    lede:
      "可承诺捐出部分收入、每笔销售的凑整捐款，或固定金额。Plug 会读取你的 Stripe 结算数据、遵守你设定的上限、按你选择的机构分配款项，" +
      "并在每次成功付款后发出收据。",
    ctaPrimary: "发布捐助规则",
    ctaSecondary: "联络我们团队",
    note: "发布规则不收费用。规则公开前，我们会先核实你的商业登记。",
    shot: "管理界面 · 捐助规则",
    rules: {
      kicker: "选择你的规则",
      h2: "三种承诺方式，一套报告系统。",
      lede: "账目有变时可随时转换，捐助频率亦可另行选择。",
      cards: [
        {
          tag: "最常用",
          headline: "1–3%",
          title: "每月收入的一定比例",
          txt: "按已结算收入计算，生意好时多捐一点，淡季时自动减少。",
          note: "须连接 Shopify 商店。设定上限后，捐款金额不会超出。",
        },
        {
          tag: "面向顾客",
          headline: "0.5–5%",
          title: "每笔销售的凑整捐款",
          txt: "每笔销售凑整至最接近的一元，差额即为捐款，让顾客看见自己的消费带来了什么——费用由你承担，不会加到顾客身上。",
          note: "须连接 Shopify 商店。绝不向顾客额外收费。",
        },
        {
          tag: "可预算",
          headline: "固定",
          title: "固定金额",
          txt: "一个议定金额，不受营业额影响——最方便财务部门规划。多久捐一次由你决定。",
          note: "可以每月捐助，亦可为单一活动作一次性捐助。",
        },
      ],
    },
    customers: {
      kicker: "顾客会看见什么",
      h2: "看得见的善行，而不只是口号。",
      lede:
        "你的规则会以标章形式出现在店面、以附注出现在收据上，并设有公开页面列出你资助的机构及已送达的金额。捐款由你支付——顾客只会知道自己的消费带来了什么，" +
        "不会被收取任何费用。",
      checks: [
        "店面标章及收据附注，提供三种语言",
        "让员工每季提名一家机构加入分配名单",
        "审计师可直接采用的企业社会责任报告",
      ],
      badgeTitle: "店面标章",
      badgeTxt: "“我们把每笔销售的 2% 捐给 3 家香港非营利机构”",
      receiptLabel: "收据附注",
      subtotalLabel: "小计",
      subtotalValue: "HK$248.00",
      totalLabel: "应付总额",
      totalValue: "HK$248.00",
      receiptFootBefore: "多谢支持——这次购物代表",
      receiptFootWe: "我们",
      receiptFootMid: "会捐出 ",
      receiptFootAmount: "HK$4.96",
      receiptFootAfter: " 给 3 家香港非营利机构，你的账单并无额外收费。",
      photo: "照片 · 店面与标章",
    },
    directory: {
      kicker: "同行伙伴",
      h2: "已经透过 Plug 行善的商户。",
      link: "查看完整名单 →",
      rows: [
        {
          name: "龙昌饼店",
          industry: "餐饮",
          district: "深水埗",
          rule: "收入的 2%",
        },
        {
          name: "港岛牙科诊所",
          industry: "医疗健康",
          district: "中环",
          rule: "每月 HK$10,000",
        },
        {
          name: "观塘印刷公司",
          industry: "服务业",
          district: "观塘",
          rule: "收入的 2.5%",
        },
        {
          name: "嘉道理咖啡烘焙",
          industry: "餐饮",
          district: "油麻地",
          rule: "每笔销售的 1.5%",
        },
      ],
    },
    quote: {
      txt: "“以前我们只在圣诞节挑一家慈善机构捐款，其余时间就忘记了。现在每月自动捐出收入的 2%，报告亦自动生成——顾客结账时还会主动问起。”",
      mono: "LC",
      name: "店主",
      meta: "龙昌饼店 · 深水埗 · 自 2021 年起捐助",
      photo: "照片",
    },
  },

  nonprofit: {
    pill: "只限第 88 条注册慈善团体",
    h1: "稳定的定期支持，账目清晰完整。",
    ledeParts: [
      "登记上架后，你的机构便会成为众多透过 Plug 捐款的个人及商户的选项之一。款项以单一清晰转账到账，并附逐项列明的结算表——流程无缝又方便。",
      "我们希望协助非营利机构筹得更多资源，因此已认证的非营利机构可完全免费在 Plug 上架！",
    ],
    ctaPrimary: "加入 Plug",
    ctaSecondary: "下载简介单张",
    note: "免费上架，亦无月费。结算表会并列显示捐款总额与实收金额。",
    shot: "管理界面 · 资金来源",
    why: {
      kicker: "为何选择 Plug",
      h2: "更多方法，资助你的使命。",
      cards: [
        {
          id: "storefront",
          title: "来自商户的无缝捐助",
          txt: "店铺与公司透过 Plug 承诺捐出部分收入。他们生意愈好，你的资金亦愈多，无须额外开口募捐。",
        },
        {
          id: "heart",
          title: "来自个人捐款者的定期捐款",
          txt: "捐款者只需设定一次金额，捐款便会持续，直至他们更改为止；不需每次都筹办新活动。",
        },
        {
          id: "people",
          title: "提升慈善合作的曝光机会",
          txt: "你的使命页面、最新动态与单位成本集中一处，方便商户与捐款者找到你并展开合作。",
        },
        {
          id: "coin",
          title: "无上架费，无月费",
          txt: "上架及营运页面完全免费。Plug 只就你实际收到的捐款收取平台费用。",
        },
      ],
    },
    dashboard: {
      kicker: "你的管理界面",
      h2: "清楚看见谁在支持你，以及支持的方式。",
      lede: "定期与单次、个人与商户、每月各项捐助规则带来多少款项，以及哪些支持者已停止捐款——让你在预算受影响前先知道。",
      checks: [
        "单一 Stripe 转账，附上审计师可直接采用的结算表",
        "公布单位成本，让捐款者知道 HK$120 实际换来什么",
        "发布服务动态，并显示在每位支持者的成效报告中",
      ],
      panelLabel: "本月收到",
      panelValue: "HK$142,800",
      individuals: {
        name: "个人",
        meta: "3,240 位把部分消费化为捐款的人",
        pct: "62%",
      },
      businesses: {
        name: "商户",
        meta: "84 家已公布捐助规则",
        pct: "38%",
      },
      stats: [
        {
          num: "+186",
          txt: "本月新增支持者",
        },
        {
          num: "HK$96,400",
          txt: "属定期捐款，收入可预算",
        },
      ],
    },
    checkplus: {
      pill: "深度整合 CHECKPLUS",
      h2: "连接 CheckPlus，后台工作自动运作。",
      lede:
        "Plug 由 CheckPlus 团队开发，两者完全打通。连接账户后，Plug 会直接沿用你现有的收据模板、报告、捐款页面及筹款活动，" +
        "无须导出文件或重新输入资料。",
      link: "了解 CheckPlus 整合 →",
      features: [
        {
          title: "自动收据",
          txt: "以你自订的模板发出第 88 条收据，每次成功付款自动寄出，不用人手按发送。",
        },
        {
          title: "会计报告",
          txt: "可直接入账的报告，格式符合审计师要求，年结时无须额外处理。",
        },
        {
          title: "完整付款资料",
          txt: "每笔交易均列明支持者、付款方式、分配比例及结算编号，一笔转账可追溯至每笔捐款。",
        },
        {
          title: "专属捐款页面",
          txt: "公益平台以外的品牌捐款页面，可用连结或二维码分享，收据自动发出。",
        },
        {
          title: "有目标的筹款活动",
          txt: "设定目标与限期，追踪进度条；分开统计，一并拨款。",
        },
        {
          title: "即时支持者纪录",
          txt: "捐款者自动成为联络人，定期计划自动成为承诺捐款，资料自动更新。",
        },
        {
          title: "一组账户登录两个平台",
          txt: "以 Plug 账户登录 CheckPlus，同一账户、同一团队，不用第二个密码。",
        },
      ],
      stripName: "CheckPlus",
      stripTxt:
        "一组账户登录两个平台。你的 Plug 账户可直接登录 CheckPlus，不用另外开户或记多一组密码。还未使用 CheckPlus？我们会在你上架时代为设定，" +
        "因为 Stripe 验证需经该平台完成。",
      stripCta: "连接账户",
    },
    join: {
      kicker: "如何加入",
      h2: "约一星期完成上架。",
      lede: "这一星期主要是我们核对文件，而不是你填表格。",
      steps: [
        {
          n: "1",
          title: "以第 88 条编号申请",
          txt: "提供慈善团体编号、最近期经审计账目，以及以机构名义开立的银行账户，填表只需十分钟。",
        },
        {
          n: "2",
          title: "经 CheckPlus 完成 Stripe 验证",
          txt: "我们会按税务局名单核实你的编号，Stripe 验证会在同一流程中透过 CheckPlus 完成，无论你是否新用户。",
        },
        {
          n: "3",
          title: "公布你的单位成本",
          txt: "让捐款者知道 HK$120 或 HK$800 实际换来什么，这些数字会出现在每份成效报告中。",
        },
      ],
      eligibility: "资格：你的机构必须是在香港注册，并根据《税务条例》第 88 条获豁免缴税的慈善团体。我们会按税务局名单核实编号，并查核你最近期的经审计账目。",
      eligibilityCta: "查看申请资格",
    },
    quote: {
      txt: "“Plug 取代了我们整套捐款行政流程。八十四家商户和三千位个人捐款者的款项化为一笔转账，结算表更可让审计师直接采用。”",
      mono: "HS",
      name: "财务主管",
      meta: "港湾庇护信托 · 深水埗",
      photo: "照片",
    },
  },

  causes: {
    homeless: {
      label: "无家可归",
      blurb: "宿位、外展服务及个案跟进，协助入住稳定居所。",
    },
    poverty: {
      label: "贫穷与饥饿",
      blurb: "剩食回收、社区饭堂及低收入家庭现金援助。",
    },
    disability: {
      label: "残疾支援",
      blurb: "自立生活、就业指导及传译服务，有需要时免费提供。",
    },
    youth: {
      label: "青年与教育",
      blurb: "自修空间、补习支援及毕业生有薪学徒计划。",
    },
    mental: {
      label: "精神健康",
      blurb: "按能力收费的心理治疗及四种语言的 24 小时危机支援热线。",
    },
    elderly: {
      label: "长者服务",
      blurb: "家访、热食送递及独居长者陪伴。",
    },
    environment: {
      label: "环境保护",
      blurb: "海岸修复、减废及城市生物多样性。",
    },
    animals: {
      label: "动物福利",
      blurb: "被遗弃动物的拯救、兽医护理及领养安置。",
    },
  },

  outcomes: {
    shelter: {
      name: "港湾庇护信托",
      one: "个安全宿位晚上",
      many: "个安全宿位晚上",
    },
    food: {
      name: "九龙剩食回收",
      one: "份剩食送递餐膳",
      many: "份剩食送递餐膳",
    },
    counselling: {
      name: "静港辅导中心",
      one: "小时资助辅导",
      many: "小时资助辅导",
    },
    brightSteps: {
      name: "步步向前网络",
    },
  },

  faqs: {
    donors: [
      {
        q: "非营利机构需要付上架费或订阅费以使用 Plug 吗？",
        a: "不需要。只要非营利机构具备第 88 条认证，即可免费在 Plug 上架并使用其服务。",
      },
      {
        q: "我可以同时支持多家非营利机构吗？",
        a: "当然可以。只要设定一个捐款总额，加入你想支持的机构，并调整资金对每家机构的支持比例即可。若只支持一家机构，则完全不需设定百分比。",
      },
      {
        q: "我何时会收到收据？",
        a: "付款成功后即可收到。每张收据均符合第 88 条扣税资格，并涵盖该次付款的所有受助机构；即使分配给四家机构，也只会收到一份文件以方便捐款者。",
      },
      {
        q: "我可以只作单次捐款而非每月捐款吗？",
        a: "可以。你可选择单次捐款，收据将即时发出。而定期捐款可选每月、每季或每年一次。",
      },
      {
        q: "我可以更改或停止捐款吗？",
        a: "无论捐款者或商户，都可以在你的 Plug 账户内随时更改捐款金额、调整比例、增减机构或暂停定期捐款计划。",
      },
    ],
    business: [
      {
        q: "我们的捐助规则如何计算？",
        a:
          "以你的 Stripe 结算数据计算。你可选择收入的一定比例、每笔销售的凑整捐款，或固定金额，再选择捐助频率，并可自行设定上限；计算与存档由 " +
          "Plug 处理。",
      },
      {
        q: "我们需要付出什么成本？",
        a: "发布规则不收费用，亦无月费。你支付的只是规则所承诺的金额，加上每笔捐款的付款手续费。",
      },
      {
        q: "我们日后可以更改规则吗？",
        a: "可以。账目有变时，你可随时转换规则类型、调整百分比、更改上限或重新分配机构比重，新规则于下个月生效。",
      },
      {
        q: "员工可以参与挑选受助机构吗？",
        a: "开启员工提名功能后，团队可每季投票选出一家机构加入分配名单。该机构包含在同一总额内，你的承诺金额不变。",
      },
      {
        q: "企业社会责任报告方面我们可获得什么？",
        a: "每月结算表、每次成功付款的收据，以及可分享的成效报告，列出受助机构及款项换来的成果。",
      },
    ],
    nonprofit: [
      {
        q: "哪些机构符合上架资格？",
        a: "在香港注册并根据《税务条例》第 88 条获豁免缴税的慈善团体。我们会按税务局名单核实编号，并查核你最近期的经审计账目。",
      },
      {
        q: "上架需要多少费用？",
        a: "加入不收费用，亦无月费。信用卡手续费会由支付服务商在款项到账前扣除；结算表会并列显示捐款总额与实收金额。",
      },
      {
        q: "我们何时及如何收到款项？",
        a: "所有捐款会合并为一笔 Stripe 转账，并附逐项列明的结算表，你只需核对一笔入账，而非数以千计的小额款项。",
      },
      {
        q: "上架是否必须使用 CheckPlus？",
        a:
          "不需要。由于 Stripe 验证需经 CheckPlus 完成，我们会在你上架时代为开设账户；你的 Plug 登录资料同样适用，" +
          "不用管理第二组密码。收据、报告、捐款页面及筹款活动则可随时启用。",
      },
      {
        q: "我们可以看到支持者是谁吗？",
        a: "商户一律显示，并列明其捐助规则。个人捐款者若同意公开便会显示姓名，否则以匿名显示；无论如何，金额与日期都会逐项列出。",
      },
      {
        q: "什么是单位成本？是否必须公布？",
        a: "单位成本是以简单语言说明你的服务价格——HK$120 或 HK$800 可以换来什么。这是必填项目，因为它能把捐款金额转化为捐款者信得过的一句说明。",
      },
    ],
  },

  sectionLabels: {
    flow: "如何联系三方",
    sides: "三方各自得到什么",
    values: "为何选择 Plug",
    how: "运作方式",
    calculator: "你的捐款可以换来什么",
    causes: "公益平台",
    impact: "公益成效",
    rules: "选择你的规则",
    customers: "顾客会看见什么",
    directory: "同行伙伴",
    why: "为何在 Plug 上架",
    dashboard: "你的管理界面",
    checkplus: "CheckPlus 整合",
    join: "如何加入",
    faq: "常见问题",
  },

  faq: {
    title: "常见问题",
    lede: "如有其他疑问，欢迎联系 Plug 团队。",
    contact: "联络客户支援 →",
  },

  cta: {
    about: {
      headline: "无论你是哪一方，一切由建立账户开始。",
      sub: "商户、非营利机构与捐款者都请在此处建立账户，加入 Plug 生态圈，一同为社会公益出一分力！",
      primary: "免费建立账户",
      secondary: "浏览非营利机构",
    },
    donors: {
      headline: "由每月 HK$100 开始，首张收据当日送达。",
      sub: "个人、商户与非营利机构都在同一处注册——我们只会问你一次身份。",
      primary: "免费建立账户",
      secondary: "浏览非营利机构",
    },
    business: {
      headline: "这星期发布规则，全年自动报告。",
      sub: "我们核实你的商业登记，你选择受助机构，捐助便会由下一个周期起按 Stripe 结算数据自动运作。",
      primary: "发布捐助规则",
      secondary: "联络我们团队",
    },
    nonprofit: {
      headline: "登记上架，不用再四处追捐款。",
      sub: "免费上架、无月费，每月一笔转账，结算表可让审计师直接采用。",
      primary: "加入 Plug",
      secondary: "查看申请资格",
    },
  },

  footer: {
    blurb: "属于香港的公益平台。",
    onThisPageHead: "本页内容",
    whoHead: "适合对象",
    whoAbout: "什么是 Plug",
    whoDonors: "捐款者",
    whoBusiness: "商户",
    whoNonprofit: "非营利机构",
    whoReports: "公益成效报告",
    trustHead: "信任与保障",
    trustSecurity: "信息安全",
    trustStripe: "Stripe 合作伙伴",
    trustSection88: "税务条例第 88 条认证",
    trustPrivacy: "隐私政策",
    companyHead: "关于我们",
    companyAbout: "公司简介",
    companyContact: "联络我们",
    companySupport: "支持香港",
    companyTerms: "服务条款",
    note: "© 2026 Plug。非营利机构须为香港注册并符合《税务条例》第 88 条的免税慈善团体，方可接收款项。",
    region: "香港 · 港币",
  },
};

export default copyCn;
